/**
 * Phase 2b-3 — the Copilot sidecar for the Artifact Studio editor.
 *
 * Reuses the proven shanks chat primitives (useShanksChat + ChatThread +
 * ComposerInput) so Copilot drafts content as suggestions. When the agent
 * calls the write-to-document tool, ChatThread → ToolPart → DocumentWriteCard
 * renders an Accept/Reject card that applies the edit to the open OnlyOffice
 * document via the connector context — the user accepts before anything lands
 * (vision §12: Copilot drafts, humans approve).
 *
 * The conversation is primed with the open-document context so the agent
 * targets THIS document/asset with write-to-document.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useCallback, useMemo, useState } from 'react';
import { useShanksChatConnect } from '../../hooks/useShanksChatConnect';
import { ChatThread } from '../ChatView/ChatThread';
import { convertShanksToChatMessages } from '../ChatView/shanksMessageAdapter';
import { ComposerInput } from '../ComposerInput';
export function ArtifactStudioCopilot({ projectId, documentId, artifactType, title }) {
    const [chatId] = useState(()=>crypto.randomUUID());
    const shanks = useShanksChatConnect({
        chatId,
        projectId
    });
    const messages = useMemo(()=>convertShanksToChatMessages(shanks.messages), [
        shanks.messages
    ]);
    // Prime the agent with the open-document context so its write-to-document
    // calls target THIS document (the tool takes a documentId param it reads
    // from context). Prepended to each user turn, same shape as the proposal
    // sidebar's `[Context: …]` prefix.
    const contextPrefix = useMemo(()=>`[Artifact Studio: you are editing the open ${artifactType || 'document'} ` + `"${title}". When you draft or edit content, call the write-to-document ` + `tool with documentId="${documentId}" so the changes land in the open ` + 'editor as a suggestion the user accepts.]', [
        artifactType,
        title,
        documentId
    ]);
    // Accept/Reject/Comment on a DocumentWriteCard continues the edit-loop by
    // sending the feedback back to shanks. Scoped to THIS Copilot's conversation
    // via ChatThread so it can't leak into another mounted chat surface.
    const sendCardFeedback = useCallback((message)=>{
        if (!shanks.isLoading) shanks.sendMessage(message);
    }, [
        shanks
    ]);
    const handleSubmit = useCallback(async (data)=>{
        const content = data.prompt.trim();
        if (!content) return;
        await shanks.sendMessage(`${contextPrefix} ${content}`);
    }, [
        shanks,
        contextPrefix
    ]);
    // Hidden stream-phase sentinel (parity with the proposal AI sidebar) so
    // tests can wait on `chat-stream-idle` — the stream is fully done — rather
    // than racing the first text delta.
    // useShanksChatConnect (sabaody transport) exposes a single isLoading flag
    // rather than the express bridge's fine-grained streamingPhase; the only
    // sentinel tests wait on is chat-stream-idle (stream fully complete).
    const phaseTestid = shanks.isLoading ? Testids.assistant.chatStreamStreaming : Testids.assistant.chatStreamIdle;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full min-h-0 flex-col",
        children: [
            /*#__PURE__*/ _jsx("span", {
                "data-testid": phaseTestid,
                "aria-hidden": "true",
                className: "hidden"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 py-2",
                children: [
                    messages.length > 0 ? /*#__PURE__*/ _jsx(ChatThread, {
                        messages: messages,
                        isSending: shanks.isLoading,
                        chatId: chatId,
                        onFeedback: sendCardFeedback
                    }) : /*#__PURE__*/ _jsx("p", {
                        className: "px-2 py-3 text-xs text-muted-foreground",
                        children: "Ask Copilot to draft a section, fill in the matrix, or revise the document. Suggestions appear here for you to accept before they land."
                    }),
                    shanks.error && /*#__PURE__*/ _jsx("p", {
                        className: "px-2 py-1 text-xs text-destructive",
                        children: shanks.error
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "border-t p-2",
                children: /*#__PURE__*/ _jsx(ComposerInput, {
                    onSubmit: handleSubmit,
                    submitting: shanks.isLoading,
                    mode: "chat",
                    submitLabel: "Send"
                })
            })
        ]
    });
}
